import { useEffect } from 'react';

import BuilderThemeProvider, {
  VIEW_AS,
} from 'hb-builder/components/common/BuilderThemeProvider';
import {
  BuilderConfigProvider,
  DeviceModeProvider,
  LazyLoadSectionProvider,
  RendererProvider,
} from 'hb-builder/contexts';
import type { Hivebrite } from 'hb-builder/types';
import { extractAndLoadFonts } from 'hb-builder/utils/fonts';
import { getBodyItemInDocument } from 'hb-builder/utils/getBodyItemInDocument';
import { getWorkspaceConfig } from 'hb-react/backoffice/components/PageBuilder/constants/workspace';
import { getCustomFontName } from 'hb-react/backoffice/components/PageBuilder/utils/getCustomFontName';
import { DEFAULT_TEMPLATE_FRAME_WIDTH } from 'hb-react/backoffice/constants/builder/defaultTemplateWidth';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import {
  BUILDER_CONTEXT_ID,
  BUILDER_DOCUMENT_TYPE,
} from 'hb-react/backoffice/types/builder/builderDocument';
import { BuilderErrorBoundary } from 'hb-react/shared/components/ErrorBoundary';
import { APP_TYPE } from 'hb-react/types/appType';

export type RendererProps = Pick<
  Hivebrite,
  'ids' | 'networkColors' | 'networkFont' | 'networkTheming'
> & {
  app: APP_TYPE;
  document: DocumentItem;
  isMobilePreview?: boolean;
  networkRootUrl?: string | null;
  scaleRatio?: number;
  subdomainNetworkUrl?: string;
  whiteLabelUrl?: string | null;
};

const Renderer = ({
  app,
  document,
  ids,
  isMobilePreview = false,
  networkColors,
  networkFont,
  networkRootUrl,
  networkTheming,
  scaleRatio = 1,
  subdomainNetworkUrl,
  whiteLabelUrl,
}: RendererProps) => {
  useEffect(() => {
    const customFontName = networkTheming && getCustomFontName(networkTheming);

    extractAndLoadFonts(document, undefined, customFontName);
  }, [document, networkTheming]);

  const isPublic = app === APP_TYPE.PUBLIC_OFFICE;

  const body = getBodyItemInDocument(document);

  return (
    <DeviceModeProvider>
      <BuilderConfigProvider
        config={{
          // @ts-expect-error "scope" is missing below but required in type
          hivebrite: {
            ids,
            networkColors,
            networkFont,
            networkTheming,
          },
        }}
      >
        <BuilderThemeProvider viewAs={VIEW_AS.USER}>
          <BuilderErrorBoundary>
            <RendererProvider
              bodyWidth={
                body?.attributes?.bodyWidth ||
                DEFAULT_TEMPLATE_FRAME_WIDTH[BUILDER_DOCUMENT_TYPE.EMAIL]
              }
              ids={ids}
              isMobilePreview={isMobilePreview}
              isPublic={isPublic}
              networkFont={networkFont}
              networkRootUrl={networkRootUrl}
              scaleRatio={scaleRatio}
              subdomainNetworkUrl={subdomainNetworkUrl}
              whiteLabelUrl={whiteLabelUrl}
              workspaceConfig={getWorkspaceConfig({
                isPageEvent: ids.context === BUILDER_CONTEXT_ID.PAGE_EVENT,
              })}
            >
              <LazyLoadSectionProvider document={document} />
            </RendererProvider>
          </BuilderErrorBoundary>
        </BuilderThemeProvider>
      </BuilderConfigProvider>
    </DeviceModeProvider>
  );
};

export default Renderer;
